/**
 * Floating "Agente JanusOS" widget — the DATA agent (CRM data Q&A).
 *
 * Sibling of `SupportAssistantWidget`, modelled on the same look + drawer
 * + a11y, but wired to the portal DATA agent instead of the manual-backed
 * Support Assistant:
 *
 *   - FAB at fixed right:24 bottom:152 — i.e. STACKED ABOVE the Support
 *     Assistant FAB (which sits at right:24 bottom:88). Icon is the JanusOS
 *     logo mark (`<JanusLogoMark />`).
 *   - Right-anchored non-modal drawer (mask={false}) so the user can keep
 *     reading the page while chatting.
 *   - Streams via `agentApi.chatStream` (`/api/v1/agent/chat/stream`) — the
 *     same client the full-page `/agent` chat uses — so assistant turns get
 *     the SAME rich rendering: markdown + janus: cards (`MessageContent`)
 *     and the collapsible tool-call trace (`ToolStepsPanel`).
 *   - State in `useJanusAgentStore` (Zustand). No persisted history.
 *
 * Hand-off to the Support Assistant (platform/how-to questions):
 *   (a) A persistent header button "Falar com o Assistente JanusOS" opens
 *       the Support Assistant and forwards the current draft / last question.
 *   (b) When a streamed turn carries `handoff_to_assistant: true` (read from
 *       the `done` payload or a tool step — see useJanusAgentChat), the
 *       bubble surfaces an inline "Continuar no Assistente JanusOS →"
 *       button that opens the assistant pre-filled with the user's last
 *       message. The mechanism is inert when the flag is absent.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Drawer, Input, Tooltip, Typography, message as antdMessage } from 'antd';
import { ArrowRight, LifeBuoy, Maximize2, Minimize2, Paperclip, RefreshCw, Send, User, X } from 'lucide-react';
/**
 * A bolha rica do Eka é a porta de markdown + realce de sintaxe.
 *
 * MEDIDO no grafo estático: `main.tsx → App.tsx → AppLayout →
 * JanusAgentWidget → MessageContent` arrasta `react-markdown`,
 * `remark-gfm`, `rehype-highlight` e `highlight.js` para o primeiro
 * carregamento de TODA página autenticada — para renderizar uma mensagem
 * que só existe depois de o usuário abrir o chat e perguntar algo.
 *
 * O gatilho fica EAGER de propósito: o botão flutuante precisa aparecer
 * junto com a tela. O que vira `lazy` é só o que desenha a MENSAGEM — e
 * uma mensagem, por definição, chega depois de uma ida ao servidor.
 */
const MessageContent = React.lazy(() => import('../../modules/agent/components/MessageContent'));
import ToolStepsPanel from '../../modules/agent/components/ToolStepsPanel';
import { useJanusAgentStore } from '../stores/useJanusAgentStore';
import { useJanusAgentChat } from '../hooks/useJanusAgentChat';
import { useSupportAssistantStore } from '../stores/useSupportAssistantStore';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { avisoDeDescasamento, destinoDoAnexo } from '../lib/destinoDoAnexo';
import { anexarPeloChat } from '../lib/anexarPeloChat';
import { useReducedMotion } from '../hooks/useReducedMotion';
import JanusLogoMark from './JanusLogoMark';

const { Text } = Typography;
const { TextArea } = Input;

// ── Design tokens (mirrors SupportAssistantWidget) ──────────────────────
const BRAND = {
  from: '#6366F1', // indigo-500
  to: '#8B5CF6', // violet-500
};
const INK = {
  900: '#0F172A',
  700: '#334155',
  500: '#64748B',
  400: '#94A3B8',
  200: '#E2E8F0',
  100: '#F1F5F9',
  50: '#F8FAFC',
};

const WIDTH = 420;
const WIDTH_EXPANDED = 840;

function firstNameFrom(
  user: { firstName?: string; name?: string; email?: string } | null,
): string {
  if (!user) return 'você';
  const fn = user.firstName?.trim();
  if (fn) return fn;
  const name = user.name?.trim();
  if (name) return name.split(/\s+/)[0];
  return user.email?.split('@')[0] ?? 'você';
}

// ── One-shot keyframes + scoped markdown overrides ──────────────────────
const STYLE_ID = 'janus-agent-widget-keyframes';
function ensureKeyframes(disableMotion: boolean) {
  if (typeof document === 'undefined') return;
  const existing = document.getElementById(STYLE_ID);
  if (existing) {
    existing.setAttribute('data-reduced', disableMotion ? '1' : '0');
    return;
  }
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.setAttribute('data-reduced', disableMotion ? '1' : '0');
  el.textContent = `
    @keyframes ja-fadein {
      from { opacity: 0; transform: translateY(4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes ja-pulse-halo {
      0%, 100% { box-shadow: 0 8px 24px rgba(99,102,241,0.35), 0 0 0 0 rgba(139,92,246,0.45); }
      50%      { box-shadow: 0 8px 24px rgba(99,102,241,0.45), 0 0 0 10px rgba(139,92,246,0); }
    }
    @keyframes ja-dot {
      0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
      40%           { transform: scale(1.0); opacity: 1; }
    }
    .ja-msg { animation: ja-fadein 180ms ease-out; }
    .ja-fab { animation: ja-pulse-halo 2.6s ease-in-out infinite; }
    .ja-fab:hover { transform: scale(1.06); }
    .ja-icon-btn:hover { background: ${INK[50]}; }
    .ja-send:hover:not(:disabled) { filter: brightness(1.08); }
    .ja-send:disabled { cursor: not-allowed; opacity: 0.5; }
    .ja-handoff:hover { background: ${INK[50]}; border-color: ${BRAND.from}; }
    .ja-header-btn:hover { background: rgba(99,102,241,0.08); border-color: ${BRAND.from}; }
    .ja-typing span {
      display: inline-block; width: 6px; height: 6px; margin-right: 3px;
      border-radius: 50%; background: ${INK[400]};
      animation: ja-dot 1.2s infinite ease-in-out;
    }
    .ja-typing span:nth-child(2) { animation-delay: 0.15s; }
    .ja-typing span:nth-child(3) { animation-delay: 0.30s; }
    .ja-input-wrap:focus-within {
      border-color: ${BRAND.from};
      box-shadow: 0 0 0 3px rgba(99,102,241,0.15);
    }
    /* Honor prefers-reduced-motion: kill looping/entrance animations. */
    [data-reduced="1"].ja-fab,
    [data-reduced="1"] .ja-fab { animation: none !important; }
    [data-reduced="1"] .ja-msg { animation: none !important; }
    [data-reduced="1"] .ja-typing span { animation: none !important; opacity: 0.6; }
  `;
  document.head.appendChild(el);
}

// ── Typing indicator ────────────────────────────────────────────────────
const TypingDots: React.FC = () => (
  <span className="ja-typing" aria-label="Digitando">
    <span /> <span /> <span />
  </span>
);

// ── Hand-off affordance (inline on a flagged assistant bubble) ──────────
const HandoffCallout: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <div style={{ marginTop: 10 }}>
    <button
      type="button"
      className="ja-handoff"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '7px 12px',
        borderRadius: 999,
        border: `1px solid ${INK[200]}`,
        background: '#FFFFFF',
        color: BRAND.from,
        fontSize: 12.5,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'background 120ms ease, border-color 120ms ease',
      }}
    >
      Continuar no Assistente JanusOS <ArrowRight size={14} />
    </button>
  </div>
);

// ── Message bubble ──────────────────────────────────────────────────────
const MessageBubble: React.FC<{
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  steps?: import('../../modules/agent/services/agentApi').AgentToolStep[];
  handoff?: boolean;
  onHandoff: () => void;
}> = ({ role, content, isStreaming, steps, handoff, onHandoff }) => {
  const isUser = role === 'user';
  const showHandoff = !isUser && !isStreaming && !!handoff;

  return (
    <div
      className="ja-msg"
      style={{
        display: 'flex',
        gap: 10,
        flexDirection: isUser ? 'row-reverse' : 'row',
        marginBottom: 14,
      }}
    >
      {/* Avatar */}
      <div
        style={{
          flexShrink: 0,
          width: 30,
          height: 30,
          borderRadius: 15,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: isUser ? `linear-gradient(135deg, ${BRAND.from} 0%, ${BRAND.to} 100%)` : '#FFFFFF',
          border: isUser ? 'none' : `1px solid ${INK[200]}`,
          marginTop: 2,
        }}
      >
        {isUser ? <User size={15} color="#FFFFFF" /> : <JanusLogoMark size={22} />}
      </div>

      <div style={{ maxWidth: '82%', minWidth: 0 }}>
        {!isUser && steps && steps.length > 0 ? <ToolStepsPanel steps={steps} /> : null}
        <div
          style={
            isUser
              ? {
                  padding: '10px 14px',
                  borderRadius: '16px 16px 4px 16px',
                  background: `linear-gradient(135deg, ${BRAND.from} 0%, ${BRAND.to} 100%)`,
                  color: '#FFFFFF',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: 1.55,
                  fontSize: 14,
                  boxShadow: '0 1px 2px rgba(15,23,42,0.06), 0 2px 8px rgba(99,102,241,0.18)',
                }
              : {
                  padding: '12px 14px',
                  borderRadius: '16px 16px 16px 4px',
                  background: '#FFFFFF',
                  color: INK[900],
                  wordBreak: 'break-word',
                  fontSize: 14,
                  lineHeight: 1.55,
                  border: `1px solid ${INK[200]}`,
                  boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.04)',
                }
          }
        >
          {isUser ? (
            content ? (
              <span>{content}</span>
            ) : null
          ) : content ? (
            <>
              {/* `null` no fallback: a bolha já está na tela e o texto chega
                  logo atrás; um esqueleto piscaria a cada mensagem. */}
              <React.Suspense fallback={null}>
                <MessageContent content={content} />
              </React.Suspense>
              {isStreaming ? <span style={{ opacity: 0.6 }}>▌</span> : null}
            </>
          ) : isStreaming ? (
            <TypingDots />
          ) : null}

          {showHandoff ? <HandoffCallout onClick={onHandoff} /> : null}
        </div>
      </div>
    </div>
  );
};

// ── Suggestion chip ─────────────────────────────────────────────────────
const SuggestionChip: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      textAlign: 'left',
      width: '100%',
      padding: '10px 12px',
      borderRadius: 10,
      border: `1px solid ${INK[200]}`,
      background: '#FFFFFF',
      color: INK[700],
      fontSize: 13,
      cursor: 'pointer',
      transition: 'background 120ms ease, border-color 120ms ease',
    }}
  >
    {label}
  </button>
);

const SUGGESTIONS = [
  'Resuma meu pipeline atual',
  'Quais deals estão em risco?',
  'Liste meus prospects mais quentes',
  'Qual o valor total em negociação?',
];

// ── Main widget ─────────────────────────────────────────────────────────
const JanusAgentWidget: React.FC = () => {
  const auth = useAuth();
  const reducedMotion = useReducedMotion();

  const isOpen = useJanusAgentStore((s) => s.isOpen);
  const open = useJanusAgentStore((s) => s.open);
  const close = useJanusAgentStore((s) => s.close);
  const resetConversation = useJanusAgentStore((s) => s.resetConversation);
  const messages = useJanusAgentStore((s) => s.messages);
  const errorMessage = useJanusAgentStore((s) => s.errorMessage);

  const { send, isSending } = useJanusAgentChat();
  const location = useLocation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [anexando, setAnexando] = useState(false);

  // Para ONDE o arquivo vai. O widget e GLOBAL — flutua em toda tela —
  // entao o destino sai do contexto, e quando nao ha contexto a resposta
  // e PERGUNTAR, nunca adivinhar. Ver `lib/destinoDoAnexo`.
  const destino = useMemo(
    () => destinoDoAnexo(location.pathname),
    [location.pathname],
  );

  const onPickFile = useCallback(() => fileInputRef.current?.click(), []);

  const onFileChosen = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const arquivo = e.target.files?.[0];
      // Zera para permitir re-anexar o MESMO arquivo depois.
      e.target.value = '';
      if (!arquivo) return;

      if (destino.tipo === 'perguntar') {
        // Sem contexto nao se escolhe por conta: o agente pergunta. Um
        // default silencioso para marketing catalogaria um contrato como
        // peca de campanha; um default para "nada" perderia o arquivo.
        await send(
          `Quero anexar o arquivo "${arquivo.name}". Nao consegui identificar ` +
            'a que negocio ele pertence a partir da tela em que estou. ' +
            'Me pergunte a qual negocio anexar (ou se e material de marketing) ' +
            'antes de eu subir o arquivo.',
        );
        return;
      }

      setAnexando(true);
      try {
        const frase = await anexarPeloChat(arquivo, destino);
        // AVISA, nunca decide. O contexto da pessoa e declaracao de
        // intencao mais forte que a nossa inferencia a partir do nome do
        // arquivo — entao o upload acontece no destino derivado. Mas um
        // descasamento silencioso e o caro, entao a duvida aparece, sem
        // bloquear: uma imagem num deal pode ser o print de um contrato.
        const aviso = avisoDeDescasamento(destino, {
          name: arquivo.name,
          type: arquivo.type,
        });
        if (aviso) antdMessage.info(aviso, 8);
        await send(frase);
      } catch (err) {
        const doBackend = (
          err as { response?: { data?: { error?: { message?: string } } } }
        )?.response?.data?.error?.message;
        const status = (err as { response?: { status?: number } })?.response?.status;
        antdMessage.error(
          doBackend ||
            (status === 413
              ? 'Arquivo maior que o limite de 50 MB.'
              : 'Nao foi possivel anexar o arquivo agora.'),
          6,
        );
      } finally {
        setAnexando(false);
      }
    },
    [destino, send],
  );
  const [draft, setDraft] = React.useState('');
  const [isExpanded, setIsExpanded] = React.useState(false);
  React.useEffect(() => {
    if (!isOpen) setIsExpanded(false);
  }, [isOpen]);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    ensureKeyframes(reducedMotion);
  }, [reducedMotion]);

  useEffect(() => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages]);

  const firstName = useMemo(() => firstNameFrom(auth?.user ?? null), [auth?.user]);

  const onSendText = useCallback(
    async (text: string) => {
      const t = text.trim();
      if (!t) return;
      setDraft('');
      await send(t);
    },
    [send],
  );

  const onSend = useCallback(() => onSendText(draft), [draft, onSendText]);

  const onKeyDownInput = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void onSend();
    }
  };

  /**
   * Hand off to the Support Assistant. Forwards a question (current draft,
   * else the user's last message) by pre-filling the assistant's composer
   * via its store, then opens it and closes this drawer. The user reviews
   * the pre-filled text and presses Enter to send — we intentionally do
   * NOT auto-fire into the other agent. Used by both the persistent header
   * button and the inline per-bubble hand-off callout.
   */
  const handoffToAssistant = useCallback(
    (question?: string) => {
      const state = useJanusAgentStore.getState();
      const text = (question ?? draft ?? '').trim() || state.lastUserMessage || '';
      const assistant = useSupportAssistantStore.getState();
      if (text) assistant.setPrefillDraft(text);
      assistant.open();
      close();
    },
    [draft, close],
  );

  if (!auth?.user) return null;

  const targetWidth = isExpanded ? WIDTH_EXPANDED : WIDTH;
  const drawerWidth = Math.min(targetWidth, Math.max(320, window.innerWidth - 40));

  return (
    <>
      {/* FAB — stacked ABOVE the Support Assistant FAB (bottom:88 → 152) */}
      <Tooltip title="Agente JanusOS" placement="left">
        <button
          type="button"
          className={reducedMotion ? '' : 'ja-fab'}
          aria-label="Abrir Agente JanusOS"
          onClick={open}
          style={{
            position: 'fixed',
            right: 24,
            bottom: 152,
            width: 56,
            height: 56,
            borderRadius: 28,
            border: 'none',
            padding: 0,
            background: `linear-gradient(135deg, ${BRAND.from} 0%, ${BRAND.to} 100%)`,
            color: '#FFFFFF',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            boxShadow: '0 8px 24px rgba(99,102,241,0.35)',
            transition: 'transform 150ms ease',
          }}
        >
          <JanusLogoMark size={28} />
        </button>
      </Tooltip>

      <Drawer
        title={null}
        placement="right"
        width={drawerWidth}
        open={isOpen}
        onClose={close}
        mask={false}
        closable={false}
        headerStyle={{ display: 'none' }}
        styles={{
          body: { display: 'flex', flexDirection: 'column', padding: 0, background: INK[50] },
          wrapper: { boxShadow: '-12px 0 32px rgba(15,23,42,0.10)' },
        }}
      >
        {/* Custom header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '14px 16px',
            background: '#FFFFFF',
            borderBottom: `1px solid ${INK[200]}`,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <JanusLogoMark size={36} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: INK[900], lineHeight: 1.2 }}>
              Agente JanusOS
            </div>
            <div
              style={{
                fontSize: 11,
                color: INK[500],
                marginTop: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  background: '#10B981',
                  boxShadow: '0 0 0 3px rgba(16,185,129,0.18)',
                  display: 'inline-block',
                }}
              />
              Online · seus dados de CRM
            </div>
          </div>

          <Tooltip title="Nova conversa">
            <button
              type="button"
              onClick={resetConversation}
              aria-label="Iniciar nova conversa"
              className="ja-icon-btn"
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                border: 'none',
                background: 'transparent',
                color: INK[500],
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 120ms ease',
              }}
            >
              <RefreshCw size={16} />
            </button>
          </Tooltip>
          <Tooltip title={isExpanded ? 'Recolher' : 'Expandir'}>
            <button
              type="button"
              onClick={() => setIsExpanded((v) => !v)}
              aria-label={isExpanded ? 'Recolher Agente JanusOS' : 'Expandir Agente JanusOS'}
              aria-pressed={isExpanded}
              className="ja-icon-btn"
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                border: 'none',
                background: 'transparent',
                color: INK[500],
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 120ms ease',
              }}
            >
              {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
          </Tooltip>
          <Tooltip title="Fechar">
            <button
              type="button"
              onClick={close}
              aria-label="Fechar Agente JanusOS"
              className="ja-icon-btn"
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                border: 'none',
                background: 'transparent',
                color: INK[500],
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 120ms ease',
              }}
            >
              <X size={18} />
            </button>
          </Tooltip>
        </div>

        {/* Persistent hand-off bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            padding: '8px 16px',
            background: '#FFFFFF',
            borderBottom: `1px solid ${INK[100]}`,
          }}
        >
          <span style={{ fontSize: 11.5, color: INK[500] }}>
            Dúvida sobre a plataforma?
          </span>
          <button
            type="button"
            className="ja-header-btn"
            onClick={() => handoffToAssistant()}
            aria-label="Falar com o Assistente JanusOS"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 10px',
              borderRadius: 999,
              border: `1px solid ${INK[200]}`,
              background: '#FFFFFF',
              color: BRAND.from,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background 120ms ease, border-color 120ms ease',
            }}
          >
            <LifeBuoy size={13} /> Falar com o Assistente JanusOS
          </button>
        </div>

        {/* Scrollable conversation */}
        <div
          ref={scrollerRef}
          role="log"
          aria-live="polite"
          style={{ flex: 1, overflowY: 'auto', padding: '20px 16px 8px' }}
        >
          {messages.length === 0 ? (
            <div className="ja-msg">
              <div
                style={{
                  marginBottom: 18,
                  padding: '16px 14px',
                  borderRadius: 14,
                  background: '#FFFFFF',
                  border: `1px solid ${INK[200]}`,
                  boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.04)',
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 600, color: INK[900], marginBottom: 4 }}>
                  Oi, {firstName} 👋
                </div>
                <div style={{ fontSize: 13, color: INK[500], lineHeight: 1.5 }}>
                  Sou o Agente JanusOS. Pergunte sobre seus deals, prospects,
                  pipeline e atividades — eu consulto seus dados em tempo real.
                  Para dúvidas de como usar a plataforma, fale com o Assistente.
                </div>
              </div>

              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: INK[400],
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                  marginBottom: 8,
                  paddingLeft: 2,
                }}
              >
                Sugestões para começar
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {SUGGESTIONS.map((s) => (
                  <SuggestionChip key={s} label={s} onClick={() => void onSendText(s)} />
                ))}
              </div>
            </div>
          ) : (
            messages.map((m) => (
              <MessageBubble
                key={m.id}
                role={m.role}
                content={m.content}
                isStreaming={m.isStreaming}
                steps={m.steps}
                handoff={m.handoff}
                onHandoff={() => handoffToAssistant(useJanusAgentStore.getState().lastUserMessage ?? undefined)}
              />
            ))
          )}
          {errorMessage ? (
            <div style={{ marginTop: 8 }}>
              <Text type="danger" style={{ fontSize: 12 }}>
                {errorMessage}
              </Text>
            </div>
          ) : null}
        </div>

        {/* Composer */}
        <div
          style={{
            borderTop: `1px solid ${INK[200]}`,
            padding: '12px 14px 14px',
            background: '#FFFFFF',
          }}
        >
          <div
            className="ja-input-wrap"
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 8,
              padding: '6px 6px 6px 12px',
              border: `1px solid ${INK[200]}`,
              borderRadius: 14,
              background: '#FFFFFF',
              transition: 'border-color 120ms ease, box-shadow 120ms ease',
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={onFileChosen}
              aria-hidden="true"
              tabIndex={-1}
            />
            <Tooltip title={destino.rotulo}>
              <button
                type="button"
                onClick={onPickFile}
                disabled={anexando || isSending}
                /* O rotulo do destino vai no aria-label tambem: quem usa
                   leitor de tela nao ve o tooltip, e "anexar" sozinho nao
                   diz PARA ONDE o arquivo vai. */
                aria-label={`Anexar arquivo. ${destino.rotulo}`}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  border: 'none',
                  background: 'transparent',
                  color: INK[400],
                  cursor: anexando || isSending ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Paperclip size={18} style={anexando ? { opacity: 0.5 } : undefined} />
              </button>
            </Tooltip>
            <TextArea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDownInput}
              autoSize={{ minRows: 1, maxRows: 5 }}
              placeholder="Pergunte sobre seus dados…"
              disabled={isSending}
              variant="borderless"
              aria-label="Mensagem para o Agente JanusOS"
              style={{ flex: 1, padding: '6px 0', fontSize: 14, lineHeight: 1.5, resize: 'none' }}
            />
            <button
              type="button"
              className="ja-send"
              onClick={onSend}
              disabled={isSending || draft.trim().length === 0}
              aria-label="Enviar mensagem"
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                border: 'none',
                background: `linear-gradient(135deg, ${BRAND.from} 0%, ${BRAND.to} 100%)`,
                color: '#FFFFFF',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 2px 6px rgba(99,102,241,0.25)',
                transition: 'filter 120ms ease, transform 120ms ease',
              }}
            >
              <Send size={16} />
            </button>
          </div>
          <div style={{ marginTop: 8, padding: '0 2px' }}>
            <Text style={{ fontSize: 11, color: INK[400] }}>
              Enter envia · Shift+Enter quebra linha · {destino.rotulo}
            </Text>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default JanusAgentWidget;
